import React from "react";
import { Input, Field, BtnGhost } from "@/components/primitives";
import { fmtZMW } from "@/lib/helpers";
import { Plus, Trash } from "@phosphor-icons/react";

export default function LineItemsEditor({ items, setItems, skuOptions = [], taxRate = 0, setTaxRate }) {
  function update(idx, key, val) {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    setItems(next);
  }
  function addRow() {
    setItems([...items, { _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: "", sku: "", quantity: 1, unit_price: 0 }]);
  }
  function removeRow(idx) {
    setItems(items.filter((_, i) => i !== idx));
  }

  const subtotal = items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
  const tax = subtotal * (Number(taxRate || 0) / 100);
  const total = subtotal + tax;

  return (
    <div className="space-y-3">
      <div className="phizo-label">Line Items</div>
      <div className="border border-[#e4e4e7]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#f4f4f5] border-b border-[#e4e4e7]">
              <th className="text-left px-3 py-2 phizo-label">Description</th>
              <th className="text-left px-3 py-2 phizo-label w-32">SKU (opt)</th>
              <th className="text-right px-3 py-2 phizo-label w-20">Qty</th>
              <th className="text-right px-3 py-2 phizo-label w-28">Unit Price</th>
              <th className="text-right px-3 py-2 phizo-label w-28">Line Total</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-4 text-center text-xs text-[#a1a1aa]">No items. Click "Add Row".</td></tr>
            )}
            {items.map((it, idx) => (
              <tr key={it._uid || `${it.sku || "row"}-${idx}`} className="border-b border-[#e4e4e7]">
                <td className="p-1.5">
                  <Input
                    data-testid={`li-desc-${idx}`}
                    value={it.description}
                    onChange={(e) => update(idx, "description", e.target.value)}
                    placeholder="Item description"
                  />
                </td>
                <td className="p-1.5">
                  {skuOptions.length > 0 ? (
                    <select
                      data-testid={`li-sku-${idx}`}
                      className="w-full border border-[#e4e4e7] bg-white px-2 py-2 text-xs mono"
                      value={it.sku || ""}
                      onChange={(e) => {
                        const sku = e.target.value;
                        const found = skuOptions.find((s) => s.sku === sku);
                        const next = [...items];
                        next[idx] = {
                          ...next[idx],
                          sku,
                          description: found ? found.name : next[idx].description,
                          unit_price: found ? found.unit_cost : next[idx].unit_price,
                        };
                        setItems(next);
                      }}
                    >
                      <option value="">—</option>
                      {skuOptions.map((s) => <option key={s.sku} value={s.sku}>{s.sku}</option>)}
                    </select>
                  ) : (
                    <Input
                      data-testid={`li-sku-${idx}`}
                      value={it.sku || ""}
                      onChange={(e) => update(idx, "sku", e.target.value)}
                      className="mono text-xs"
                    />
                  )}
                </td>
                <td className="p-1.5">
                  <Input
                    data-testid={`li-qty-${idx}`}
                    type="number" step="0.01" min="0"
                    className="text-right mono"
                    value={it.quantity}
                    onChange={(e) => update(idx, "quantity", e.target.value)}
                  />
                </td>
                <td className="p-1.5">
                  <Input
                    data-testid={`li-price-${idx}`}
                    type="number" step="0.01" min="0"
                    className="text-right mono"
                    value={it.unit_price}
                    onChange={(e) => update(idx, "unit_price", e.target.value)}
                  />
                </td>
                <td className="text-right p-3 mono font-semibold">
                  {fmtZMW(Number(it.quantity || 0) * Number(it.unit_price || 0))}
                </td>
                <td className="text-center">
                  <BtnGhost onClick={() => removeRow(idx)} className="text-[#FF2A2A]">
                    <Trash size={14} weight="bold" />
                  </BtnGhost>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" onClick={addRow}
          data-testid="li-add-row"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:underline">
          <Plus size={14} weight="bold" /> Add Row
        </button>
        <div className="w-64 space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="phizo-label">Subtotal</span>
            <span className="mono">{fmtZMW(subtotal)}</span>
          </div>
          {setTaxRate && (
            <div className="flex items-center justify-between text-sm">
              <span className="phizo-label">Tax %</span>
              <input
                data-testid="li-tax"
                type="number" step="0.01" min="0"
                className="w-20 border border-[#e4e4e7] px-2 py-1 text-right mono text-xs"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
              />
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="phizo-label">Tax</span>
            <span className="mono">{fmtZMW(tax)}</span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[#e4e4e7]">
            <span className="phizo-label">Total</span>
            <span className="font-heading text-lg font-black">{fmtZMW(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
